
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, MapPinned, Plus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { fetchPhysicalLocations, addPhysicalLocation } from '../../store/slices/physicalLocationSlice';
import { clearPhysicalLocationAddState } from '../../store/slices/physicalLocationSlice';
import { fetchAllDropdowns } from '../../store/slices/dropdownSlice';
import { buildOptions } from '../../utils/dropdownOpts';
import EmployeeAvatar from './EmployeeAvatar';

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function labelFor(options, code) {
  if (!code) return '—';
  const hit = options.find(o => o.value === String(code).trim());
  return hit ? hit.label : code;
}

export default function PhysicalLocationModal({ serviceNo, employeeName, isOpen, onClose }) {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [divCode, setDivCode] = useState('');
  const [depCode, setDepCode] = useState('');
  const [locCode, setLocCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const { rows, loading, error, serviceNo: loadedFor } = useSelector(s => s.physicalLocation);
  const { adding, addError, addSuccess } = useSelector(s => s.physicalLocation);
  const dropdowns = useSelector(s => s.dropdowns);

  const devOpts = useMemo(() => buildOptions(dropdowns.devDetails, 'DivisionCode', 'DivisionName'), [dropdowns.devDetails]);
  const depOpts = useMemo(() => buildOptions(dropdowns.depDetails, 'p_hdd_dep_code', 'p_hdd_dep_desc'), [dropdowns.depDetails]);
  const locOpts = useMemo(() => buildOptions(dropdowns.locDetails, 'p_hld_loc_code', 'p_hld_loc_desc'), [dropdowns.locDetails]);

  useEffect(() => {
    if (!isOpen || !serviceNo) return;
    dispatch(fetchPhysicalLocations({ serviceNo }));
    if (!dropdowns.loaded && !dropdowns.loading) dispatch(fetchAllDropdowns());
    // eslint-disable-next-line
  }, [isOpen, serviceNo]);

  useEffect(() => {
    if (addSuccess) {
      dispatch(fetchPhysicalLocations({ serviceNo }));
      setShowForm(false);
      setDivCode(''); setDepCode(''); setLocCode(''); setStartDate(''); setEndDate('');
      const t = setTimeout(() => dispatch(clearPhysicalLocationAddState()), 2500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [addSuccess]);

  if (!isOpen) return null;

  const isCurrent = loadedFor === serviceNo;

  const handleSave = () => {
    dispatch(addPhysicalLocation({ serviceNo, divCode, depCode, locCode, startDate, endDate }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <EmployeeAvatar
                serviceNo={serviceNo}
                initials={(employeeName || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?'}
                size={44}
              />
              <div className="min-w-0">
                <p className="text-white font-bold text-base truncate">{employeeName || 'Employee'} — Physical Locations</p>
                <p className="text-white/60 text-xs font-mono mt-0.5">Service No: {serviceNo}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition-colors shrink-0">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 bg-gray-50 space-y-4">
          {addError && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle size={14} className="shrink-0" /> {addError}
            </div>
          )}
          {addSuccess && (
            <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm">
              <CheckCircle2 size={14} className="shrink-0" /> Physical location assigned successfully.
            </div>
          )}

          {/* Assigned locations list */}
          {loading || !isCurrent ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-9 h-9 border-4 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Loading physical locations...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle size={14} className="shrink-0" /> {error}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400 bg-white border border-dashed border-gray-200 rounded-2xl">
              <MapPinned size={32} className="opacity-30 mb-3" />
              <p className="text-sm font-semibold text-gray-500">No physical locations assigned yet</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Division</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Department</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Start Date</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">End Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.map((r, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                      <td className="px-3 py-2.5 text-gray-700 font-medium">{labelFor(devOpts, r.p_div_code)}</td>
                      <td className="px-3 py-2.5 text-gray-700">{labelFor(depOpts, r.p_dep_code)}</td>
                      <td className="px-3 py-2.5 text-gray-700">{labelFor(locOpts, r.p_loc_code)}</td>
                      <td className="px-3 py-2.5 text-gray-600">{fmtDate(r.p_hpd_start_date)}</td>
                      <td className="px-3 py-2.5">
                        {r.p_hpd_end_date
                          ? <span className="text-gray-600">{fmtDate(r.p_hpd_end_date)}</span>
                          : <span className="text-emerald-600 font-semibold text-[11px] bg-emerald-50 px-2 py-0.5 rounded-full">Ongoing</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add new physical location */}
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-slate-500 hover:text-slate-700 hover:bg-white transition-colors text-sm font-semibold"
            >
              <Plus size={15} /> Add Physical Location
            </button>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3 shadow-sm">
              <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Assign New Physical Location</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Division</label>
                  <select value={divCode} onChange={(e) => setDivCode(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                    <option value="">— Select —</option>
                    {devOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Department</label>
                  <select value={depCode} onChange={(e) => setDepCode(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                    <option value="">— Select —</option>
                    {depOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Location</label>
                  <select value={locCode} onChange={(e) => setLocCode(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100">
                    <option value="">— Select —</option>
                    {locOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Start Date</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">End Date <span className="text-gray-300 font-normal">(optional)</span></label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100" />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={adding || !divCode || !depCode || !locCode || !startDate}
                  className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {adding ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
